import { ExclamationTriangleIcon } from "@heroicons/react/24/outline";
import { useTokenBalance } from "../hooks/useTokenService";
import { cn } from "../../../shared/lib/utils";

// Custom Coins icon since it's not available in Heroicons
const CoinsIcon = ({ className }: { className?: string }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1" />
  </svg>
);

interface TokenBalanceProps {
  className?: string;
  showDetails?: boolean;
}

export function TokenBalance({ className, showDetails = false }: TokenBalanceProps) {
  const { balance, isLow, isLoading, totalAllocated, totalConsumed } = useTokenBalance();

  if (isLoading) {
    return (
      <div className={cn("flex items-center gap-2", className)}>
        <div className="w-5 h-5 rounded-full bg-muted animate-pulse" />
        <div className="w-12 h-4 bg-muted animate-pulse rounded" />
      </div>
    );
  }

  return (
    <div className={cn("flex items-center gap-2", className)}>
      <div className={cn(
        "flex items-center gap-1.5 px-2 py-1 rounded-full text-sm font-medium transition-colors",
        isLow 
          ? "bg-warning text-warning-dark dark:bg-warning/20 dark:text-warning" 
          : "bg-primary-light text-primary-dark dark:bg-primary/20 dark:text-primary"
      )}>
        <CoinsIcon className="w-4 h-4" />
        <span>{balance}</span>
        {isLow && <ExclamationTriangleIcon className="w-3 h-3" />}
      </div>
      
      {isLow && !showDetails && (
        <span className="text-xs text-orange-600 dark:text-orange-400">
          Low balance
        </span>
      )}
      
      {showDetails && (
        <div className="text-xs text-muted-foreground">
          <div>Allocated: {totalAllocated}</div>
          <div>Used: {totalConsumed}</div>
          <div>Remaining: {balance}</div>
          {isLow && (
            <div className="text-orange-600 dark:text-orange-400 font-medium">
              Running low on tokens!
            </div>
          )}
        </div>
      )}
    </div>
  );
} 